/**
 * User Feedback (AVA-349) — client-side constants.
 *
 * The cadence itself is server-side and deliberately stays there (spec: "the UI
 * never does date math"). What lives here is only what the client must know to
 * render and validate the prompt before the round-trip.
 *
 * Bounds mirror the API and must not drift:
 *   rating 1..5 and comment <= 2000  — MODS.Forge.Db.Management/UserFeedback.cs
 *   interval 1..3650, default 90     — MODS.Forge.Api/Services/Feedback/FeedbackRules.cs
 */

export const FEEDBACK_RATING = {
  MIN: 1,
  MAX: 5,
};

export const FEEDBACK_COMMENT_MAX_LENGTH = 2000;

/** Below this many characters remaining, the counter becomes visible. */
export const FEEDBACK_COUNTER_VISIBLE_FROM = 100;

export const FEEDBACK_INTERVAL_DAYS = {
  MIN: 1,
  MAX: 3650,
  DEFAULT: 90,
};

/**
 * Per-browser-session guard. The server decides eligibility; this only stops a
 * second prompt inside one session (e.g. an SSO transition after an app load).
 */
export const FEEDBACK_SESSION_KEY = 'ava.feedback.checkedThisSession';

/** A rating at or below this gets acknowledgement copy rather than a generic thanks. */
export const FEEDBACK_LOW_RATING_THRESHOLD = 2;

/**
 * Every user-facing string in one place. The app has no localization layer
 * (verified 2026-09-02: no vue-i18n, no locales, zero $t call sites), so these
 * are literals — but they are literals behind a single seam, so a future i18n
 * pass has exactly one file to change.
 */
/**
 * Page sizes offered by the results footer. Deliberately narrower than the repo
 * default (which offers up to 500 and "All"): FeedbackRules.MaxPageSize caps a
 * page at 200 server-side, so larger options would quietly return fewer rows
 * than they promise.
 */
export const FEEDBACK_RESULTS_PAGE_SIZES = [25, 50, 100, 200];

/**
 * Page size used to pull the company list for the results filter. Not a display
 * limit: it only has to be large enough to hold every company on the platform.
 */
export const FEEDBACK_COMPANY_LOOKUP_PAGE_SIZE = 500;

/**
 * v-data-table sort keys -> FeedbackSubmissionSort on the API. The API has no
 * "unsorted" state, so the table is `must-sort` and anything unmapped falls back
 * to CreateDate.
 */
export const FEEDBACK_SORT_MAP = {
  createDate: 'CreateDate',
  userDisplayName: 'UserName',
  companyName: 'CompanyName',
  rating: 'Rating',
};

export const FEEDBACK_COPY = {
  title: 'How is AVA working for you?',
  subtitle: 'A quick rating helps us decide what to fix next.',
  ratingLegend: 'Overall rating',
  ratingRequired: 'Required',
  commentLabel: 'Anything you would like to add?',
  commentOptional: 'Optional',
  commentPlaceholder: 'What is working well, or what is getting in your way?',
  ratingHint: 'Pick a rating to enable Submit.',
  submit: 'Submit',
  snooze: 'Remind me in a week',
  closeTooltip: 'Close and remind me in a week',
  submitting: 'Sending…',
  error: 'That did not send. Check your connection and try again.',
  successTitle: 'Thanks',
  successBody: 'Your feedback helps us decide what to fix next.',
  // A 1–2 star rater has just told us something is wrong. A generic "thanks"
  // reads as dismissal, so the low path names what happens to their answer.
  lowRatingTitle: 'Thanks — that is the kind of detail we act on',
  lowRatingBody: 'Your rating and note go to the AVA product team.',
  charactersRemaining: (n) => `${n} characters remaining`,
  close: 'Close',
  snoozedTitle: 'Remind me later',
  snoozedBody: 'No problem — we will ask again in a week.',
};

/** Screen-reader label for each star; the number alone tells the user nothing. */
export const FEEDBACK_RATING_LABELS = {
  1: 'Not working for me',
  2: 'Needs work',
  3: 'It does the job',
  4: 'Good',
  5: 'Excellent',
};

export default {
  FEEDBACK_RATING,
  FEEDBACK_RESULTS_PAGE_SIZES,
  FEEDBACK_SORT_MAP,
  FEEDBACK_COMMENT_MAX_LENGTH,
  FEEDBACK_COUNTER_VISIBLE_FROM,
  FEEDBACK_INTERVAL_DAYS,
  FEEDBACK_SESSION_KEY,
  FEEDBACK_LOW_RATING_THRESHOLD,
  FEEDBACK_COPY,
  FEEDBACK_RATING_LABELS,
};
